'use client';
import React, { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import useNotify from '@/hooks/useNotify';
import { Eye, EyeOff, Loader2, Lock, Mail, User, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth';
import { auth } from '@/data/config/firebase-config';

type Mode = 'login' | 'register';

const errorMessages: Record<string, string> = {
  'auth/invalid-email': 'Μη έγκυρη διεύθυνση email.',
  'auth/user-disabled': 'Ο λογαριασμός σας έχει απενεργοποιηθεί.',
  'auth/user-not-found': 'Δεν βρέθηκε λογαριασμός με αυτό το email.',
  'auth/wrong-password': 'Λανθασμένο email ή κωδικός πρόσβασης.',
  'auth/invalid-credential': 'Λανθασμένο email ή κωδικός πρόσβασης.',
  'auth/email-already-in-use': 'Υπάρχει ήδη λογαριασμός με αυτό το email.',
  'auth/weak-password': 'Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες.',
  'auth/too-many-requests': 'Πολλές προσπάθειες. Δοκιμάστε ξανά αργότερα.',
  'auth/network-request-failed': 'Πρόβλημα σύνδεσης. Ελέγξτε το δίκτυό σας.',
  'auth/popup-closed-by-user': 'Η σύνδεση ακυρώθηκε.',
};

const getErrorMessage = (error: unknown) => {
  const code = (error as { code?: string })?.code ?? '';
  return errorMessages[code] ?? 'Κάτι πήγε στραβά. Δοκιμάστε ξανά.';
};

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  error?: string;
  trailing?: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, icon: Icon, error, trailing, className, ...props }) => (
  <div className="space-y-1.5">
    <label htmlFor={props.id} className="text-xs font-medium tracking-wide text-slate-600 uppercase">
      {label}
    </label>
    <div className="group relative">
      <Icon
        className={cn(
          'pointer-events-none absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 transition-colors',
          error ? 'text-red-400' : 'text-slate-400 group-focus-within:text-purple-600',
        )}
      />
      <input
        {...props}
        aria-invalid={!!error}
        className={cn(
          'h-12 w-full rounded-xl border bg-slate-50/80 pr-11 pl-11 text-sm text-slate-900 shadow-sm transition-all duration-200 outline-none placeholder:text-slate-400',
          'hover:bg-white focus:border-purple-400 focus:bg-white focus:ring-4 focus:ring-purple-500/10',
          'disabled:cursor-not-allowed disabled:opacity-60',
          error ? 'border-red-300 focus:border-red-400 focus:ring-red-500/10' : 'border-slate-200',
          className,
        )}
      />
      {trailing}
    </div>
    <AnimatePresence>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          className="text-xs font-medium text-red-500"
        >
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  </div>
);

const LoginRight: React.FC = () => {
  const notify = useNotify();
  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isRegister = mode === 'register';

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
    setErrors({});
    setPassword('');
    setConfirmPassword('');
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    if (isRegister && !name.trim()) {
      nextErrors.name = 'Συμπληρώστε το ονοματεπώνυμό σας.';
    }
    if (!email.trim()) {
      nextErrors.email = 'Συμπληρώστε το email σας.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      nextErrors.email = 'Μη έγκυρη διεύθυνση email.';
    }
    if (!password) {
      nextErrors.password = 'Συμπληρώστε τον κωδικό σας.';
    } else if (isRegister && password.length < 6) {
      nextErrors.password = 'Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες.';
    }
    if (isRegister && confirmPassword !== password) {
      nextErrors.confirmPassword = 'Οι κωδικοί δεν ταιριάζουν.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading || !validate()) return;

    setLoading(true);
    try {
      if (isRegister) {
        const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
        await updateProfile(credential.user, { displayName: name.trim() });
        // Firebase appends this as continueUrl on the verification link, which
        // our /auth/action handler follows once the oobCode is applied.
        await sendEmailVerification(credential.user, {
          url: `${window.location.origin}/dashboard`,
          handleCodeInApp: true,
        });
        notify.success('Ο λογαριασμός σας δημιουργήθηκε! Ελέγξτε το email σας για επαλήθευση.');
      } else {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      }
    } catch (error) {
      notify.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setErrors({ email: 'Συμπληρώστε το email σας για επαναφορά κωδικού.' });
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      notify.success('Σας στείλαμε email για επαναφορά του κωδικού σας.');
    } catch (error) {
      notify.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();

    provider.addScope('email');
    provider.addScope('profile');
    provider.addScope('openid');

    // Set custom parameters after configuring scopes
    provider.setCustomParameters({
      prompt: 'select_account',
    });

    await signInWithPopup(auth, provider);
  };

  const passwordToggle = (
    <button
      type="button"
      onClick={() => setShowPassword((prev) => !prev)}
      className="absolute top-1/2 right-3.5 -translate-y-1/2 text-slate-400 transition-colors hover:text-purple-600"
      aria-label={showPassword ? 'Απόκρυψη κωδικού' : 'Εμφάνιση κωδικού'}
      tabIndex={-1}
    >
      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  return (
    <div className="flex h-full w-full flex-col justify-center overflow-y-auto px-8 py-10">
      {/* Soft ambient glow to echo the gradient panel on the left */}
      <div className="pointer-events-none absolute -top-24 -right-20 h-72 w-72 rounded-full bg-purple-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />

      <motion.div
        className="relative mx-auto w-full max-w-sm space-y-7"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        {/* Brand mark */}
        <div className="flex flex-col items-center space-y-4 text-center">
          <motion.div
            className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-purple-500/25"
            whileHover={{ scale: 1.05, rotate: 4 }}
            whileTap={{ scale: 0.95 }}
          >
            <Zap className="h-6 w-6 text-white" />
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="space-y-2"
            >
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                {isRegister ? 'Δημιουργία λογαριασμού' : 'Καλώς ήρθατε'}
              </h1>
              <p className="text-sm text-slate-500">
                {isRegister
                  ? 'Συμπληρώστε τα στοιχεία σας για να ξεκινήσετε'
                  : 'Συνδεθείτε για πρόσβαση στο σύστημα διαχείρισης ενέργειας'}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mode Switch with sliding indicator */}
        <div className="relative grid grid-cols-2 rounded-xl border border-slate-200 bg-slate-100/80 p-1">
          <motion.div
            className="absolute inset-y-1 w-[calc(50%-0.25rem)] rounded-lg bg-white shadow-sm"
            animate={{ left: isRegister ? '50%' : '0.25rem' }}
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
          />
          {(['login', 'register'] as Mode[]).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => switchMode(value)}
              className={cn(
                'relative z-10 rounded-lg py-2 text-sm font-medium transition-colors duration-200',
                mode === value ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700',
              )}
            >
              {value === 'login' ? 'Σύνδεση' : 'Εγγραφή'}
            </button>
          ))}
        </div>

        {/* Email & Password Form */}
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <AnimatePresence initial={false}>
            {isRegister && (
              <motion.div
                key="name"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <Field
                  id="name"
                  name="name"
                  type="text"
                  label="Ονοματεπώνυμο"
                  icon={User}
                  autoComplete="name"
                  placeholder="Γιώργος Παπαδόπουλος"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                  error={errors.name}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <Field
            id="email"
            name="email"
            type="email"
            label="Email"
            icon={Mail}
            autoComplete="email"
            placeholder="name@enexan.gr"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            error={errors.email}
          />

          <div className="space-y-1.5">
            <Field
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              label="Κωδικός πρόσβασης"
              icon={Lock}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              error={errors.password}
              trailing={passwordToggle}
            />
            {!isRegister && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={loading}
                  className="text-xs font-medium text-slate-500 transition-colors hover:text-purple-700 disabled:opacity-50"
                >
                  Ξεχάσατε τον κωδικό;
                </button>
              </div>
            )}
          </div>

          <AnimatePresence initial={false}>
            {isRegister && (
              <motion.div
                key="confirm"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <Field
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  label="Επιβεβαίωση κωδικού"
                  icon={Lock}
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  error={errors.confirmPassword}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.01 }}
            whileTap={{ scale: loading ? 1 : 0.99 }}
            className="group relative flex h-13 w-full items-center justify-center overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 py-3.5 text-base font-semibold text-white shadow-lg shadow-purple-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/30 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {/* Shine Effect Overlay */}
            <div className="absolute inset-0 translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-1000 group-hover:-translate-x-full" />
            <span className="relative flex items-center">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isRegister ? 'Δημιουργία λογαριασμού' : 'Σύνδεση'}
            </span>
          </motion.button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent to-slate-200" />
          <span className="text-xs font-medium tracking-wider text-slate-400 uppercase">ή</span>
          <div className="h-px flex-1 bg-gradient-to-l from-transparent to-slate-200" />
        </div>

        {/* Google Sign In with Gradient & Shine Effect */}
        <div className="space-y-4">
          <button
            onClick={handleGoogleLogin}
            className="group relative h-14 w-full overflow-hidden rounded-lg bg-gradient-to-r from-slate-900 via-purple-900 to-slate-800 text-base font-medium text-white shadow-lg transition-all duration-300 hover:from-slate-800 hover:via-purple-800 hover:to-slate-700 hover:shadow-xl"
          >
            {/* Shine Effect Overlay */}
            <div className="absolute inset-0 translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 group-hover:-translate-x-full" />

            {/* Button Content */}
            <div className="relative flex items-center justify-center">
              <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Σύνδεση με Google
            </div>
          </button>
        </div>

        {/* Terms of Service */}
        <p className="text-center text-xs leading-relaxed text-slate-400">
          Συνεχίζοντας, συμφωνείτε με τους{' '}
          <a href="#" className="font-medium text-slate-600 underline-offset-2 hover:text-purple-700 hover:underline">
            Όρους Χρήσης
          </a>{' '}
          και την{' '}
          <a href="#" className="font-medium text-slate-600 underline-offset-2 hover:text-purple-700 hover:underline">
            Πολιτική Απορρήτου
          </a>{' '}
          της Enexan Energy.
        </p>
      </motion.div>
    </div>
  );
};

export default LoginRight;
